/** @jsxImportSource @emotion/react */
import { ArrowUpOutlined, SearchOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { useDebounce } from '@sportninja/common/hooks/useDebounce'
import { useScheduleSettings } from '@sportninja/common/hooks/useScheduleSettings'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { getStatTypesBySportId } from '@sportninja/common/selectors/types'
import {
  Avatar,
  FloatButton,
  Input,
  Spin,
  Switch,
  Table,
  Tooltip,
  Typography,
} from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import { useUrlManager } from 'src/hooks/useUrlManager'
import useWindowSize from 'src/hooks/useWindowSize'
import ReprocessingSettingsOverlay from 'src/pages/Schedule/ReprocessingSettingsOverlay'
import Icon from '../../Icon'
import { Flex } from '../../Layout'
import GameListFilters from '../../List/GameList/GameListFilters'
import useGetScheduleFiltering from '../../List/useGetScheduleFiltering'
import LeadersView from './Leaders'
import StatisticsFilters from './StatisticsFilters'

const SCROLL_THRESHOLD = 500

const Statistics = ({
  autoSelectMostRecentComp,
  areFiltersHidden,
  entityType,
  entityId,
  refreshKey,
  scheduleReprocessingListener,
  isSoccer = false,
  orgId,
  isInFrame = false,
  isHockey = false,
  isGenericSport = false,
  setOutsideDismissed = () => {},
  dismissed = false,
  statTypes,
}) => {
  const { setParam } = useUrlManager()

  // Fetch schedule settings for dynamic columns
  const { scheduleSettings, fetchScheduleSettings } = useScheduleSettings()

  const { stats, leaderboards } = scheduleSettings?.settings ?? {}
  const { player: playerSettings, goalie: goalieSettings } = stats ?? {}
  const displayLeaderboards = leaderboards?.display ?? true
  const playerDisplayedIds = useMemo(
    () => playerSettings?.displayed ?? [],
    [playerSettings]
  )
  const goalieDisplayedIds = useMemo(
    () => goalieSettings?.displayed ?? [],
    [goalieSettings]
  )

  // Index statTypes by abbreviation for quick lookup
  const statTypesByAbbr = useMemo(() => {
    if (!statTypes || !Array.isArray(statTypes)) {
      return {}
    }
    return statTypes.reduce((acc, statType) => {
      if (statType.abbreviation) {
        acc[statType.abbreviation] = statType
      }
      return acc
    }, {})
  }, [statTypes])

  // Index statTypes by ID for dynamic column generation
  const statTypesById = useMemo(() => {
    if (!statTypes || !Array.isArray(statTypes)) {
      return {}
    }
    return statTypes.reduce((acc, statType) => {
      if (statType.id) {
        acc[statType.id] = statType
      }
      return acc
    }, {})
  }, [statTypes])

  // Create mapping from abbreviation to stat_id for table onChange handler
  const statIdByAbbr = useMemo(() => {
    if (!statTypes || !Array.isArray(statTypes)) {
      return {}
    }
    return statTypes.reduce((acc, statType) => {
      if (statType.abbreviation && statType.id) {
        acc[statType.abbreviation] = statType.id
      }
      return acc
    }, {})
  }, [statTypes])

  // Compute default sort from settings - NO FALLBACK
  const playerDefaultSort = useMemo(() => {
    const statId = playerSettings?.sort_order?.stat_id
    if (statId && statTypesById[statId]) {
      return statId
    }
    return null
  }, [playerSettings, statTypesById])

  const goalieDefaultSort = useMemo(() => {
    const statId = goalieSettings?.sort_order?.stat_id
    if (statId && statTypesById[statId]) {
      return statId
    }
    return null
  }, [goalieSettings, statTypesById])

  // Get sort directions based on is_sort_desc
  // If is_sort_desc is true, first click should be desc, otherwise asc
  const getSortDirections = useCallback(
    (statId) => {
      const statType = statTypesById[statId]
      if (statType?.is_sort_desc === true) {
        return ['descend', 'ascend', 'descend']
      }
      return ['ascend', 'descend', 'ascend']
    },
    [statTypesById]
  )

  // Get tooltip title from statTypes
  const getStatTooltip = useCallback(
    (abbreviation, fallbackTitle) => {
      const statType = statTypesByAbbr[abbreviation]
      if (statType?.name && statType?.description) {
        return (
          <span>
            <span
              css={css`
                font-weight: 600;
              `}
            >
              {statType.name}
            </span>{' '}
            - {statType.description}
          </span>
        )
      }
      return statType?.name || fallbackTitle
    },
    [statTypesByAbbr]
  )

  // Render stat title with tooltip above the label
  const renderStatTitle = useCallback(
    (abbr, label, fallback) => (
      <Tooltip
        title={getStatTooltip(abbr, fallback ?? label)}
        placement='top'
        mouseEnterDelay={0.1}
      >
        <span
          css={css`
            display: block;
            width: 100%;
          `}
        >
          {label}
        </span>
      </Tooltip>
    ),
    [getStatTooltip]
  )

  // Get stat value from item stats array
  const getStatValue = useCallback(
    (itemStats, abbreviation) => {
      const statType = statTypesByAbbr[abbreviation]
      const precision = statType?.precision ?? 0

      if (!itemStats || !Array.isArray(itemStats)) {
        return (0).toFixed(precision)
      }
      const stat = itemStats.find((s) => s.abbr === abbreviation)
      return stat?.value ?? (0).toFixed(precision)
    },
    [statTypesByAbbr]
  )

  // Helper function to generate columns from stat IDs
  const generateColumnsFromIds = useCallback(
    (ids, currentSortBy, currentDirection) => {
      if (!ids?.length || !statTypesById) {
        return []
      }

      return ids
        .map((id) => {
          const statType = statTypesById[id]
          if (!statType) {
            return null
          }
          const abbr = statType.abbreviation
          const label = isGenericSport && abbr === 'G' ? 'G(P)' : abbr
          const fallback =
            isGenericSport && abbr === 'G' ? 'Points/Goals' : abbr

          // Determine controlled sortOrder based on current sort state
          const isCurrentlySorted = currentSortBy === id
          const columnSortOrder = isCurrentlySorted
            ? currentDirection === 'desc'
              ? 'descend'
              : 'ascend'
            : null

          return {
            title: renderStatTitle(abbr, label, fallback),
            key: abbr,
            sorter: () => {},
            sortDirections: getSortDirections(id),
            dataIndex: abbr,
            sortOrder: columnSortOrder,
            showSorterTooltip: false,
          }
        })
        .filter(Boolean)
    },
    [getSortDirections, renderStatTitle, isGenericSport, statTypesById]
  )

  const BASE_SLUG = `/${entityType}/${entityId}/stats`

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [sortBy, setSortBy] = useState(playerDefaultSort)
  const [direction, setDirection] = useState('desc')
  const [items, setItems] = useState([])
  const [slug, setSlug] = useState(BASE_SLUG)
  const [isGlobalFilterEnabled, setIsGlobalFilterEnabled] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isAppending, setIsAppending] = useState(false)

  const [searchValue, setSearchValue] = useState('')
  const debouncedSearchTerm = useDebounce(searchValue, 500)
  const manualSearchRef = useRef(false)
  const settingsAppliedRef = useRef(false)
  const [isAggregateEnabled, setIsAggregateEnabled] = useState(false)
  const [isGoalieStats, setIsGoalieStats] = useState(true)
  const [activeTab, setActiveTab] = useState(
    displayLeaderboards ? 'leaders' : 'stats'
  )

  const isSchedule = entityType === ENTITY_TYPES.schedule
  const isTeam = entityType === ENTITY_TYPES.team
  const args = {}

  if (isSchedule) {
    args.schedule_id = entityId
  } else if (isTeam) {
    args.team_id = entityId
  }

  // Sync activeTab when displayLeaderboards changes (e.g., after settings load)
  useEffect(() => {
    if (!displayLeaderboards && activeTab === 'leaders') {
      setActiveTab('stats')
    }
  }, [displayLeaderboards, activeTab])

  // Fetch schedule settings when component mounts or entityId changes
  useEffect(() => {
    if (entityId && isSchedule) {
      fetchScheduleSettings(entityId)
    }
  }, [entityId, isSchedule, fetchScheduleSettings])

  const { width } = useWindowSize()
  const isMobile = useMemo(() => {
    return width <= 768
  }, [width])

  const onTeamChange = (teamId) => {
    setTeam(teamId)
    if (teamId) {
      setSlug(`/schedules/${entityId}/stats/team/${teamId}`)
    } else {
      setSlug(BASE_SLUG)
    }
  }

  const onScheduleChange = useCallback(
    (scheduleId) => {
      if (scheduleId) {
        setScheduleForSearch(scheduleId)
        // setSlug(BASE_SLUG)
      } else {
        setScheduleForSearch(null)
        setSlug(BASE_SLUG)
      }
    },
    [BASE_SLUG]
  )

  const onIsGoalie = (isGoalie) => {
    if (loading || isGoalie === isGoalieStats) {
      return
    }
    setItems([])
    setLoading(true)
    setIsGoalieStats(isGoalie)
  }

  const {
    teams,
    team,
    setTeam,
    filters,
    state,
    setState,
    loaded,
    currentParams,
  } = useGetScheduleFiltering(args, {
    defaultSort: 'desc',
    autoSelectMostRecentComp,
    onTeamChange,
    onScheduleChange,
    onIsGoalie,
  })

  const [scheduleForSearch, setScheduleForSearch] = useState(
    currentParams?.sn_division ?? null
  )

  // Handle tab selection - set loading when switching to stats tab
  const handleTabSelect = useCallback(
    (tab) => {
      if (tab === 'stats' && activeTab !== 'stats') {
        setLoading(true)
      }
      setActiveTab(tab)
    },
    [activeTab]
  )

  // Handle View More click from leaderboard cards
  const handleLeaderViewMore = useCallback(
    (abbreviation) => {
      if (!abbreviation) {
        return
      }
      // Convert abbreviation to stat ID using the same mapping as table columns
      const statId = statIdByAbbr[abbreviation]
      if (!statId) {
        return
      }

      // Calculate expected direction for this stat
      const statType = statTypesById[statId]
      const expectedDirection = statType?.is_sort_desc ? 'desc' : 'asc'

      // If already sorted by this stat with same direction, just switch tabs
      // (data is already loaded, no need to reset)
      if (sortBy === statId && direction === expectedDirection) {
        setActiveTab('stats')
        // Ensure division filter is reflected in Stats Filter
        if (scheduleForSearch) {
          setIsGlobalFilterEnabled(false)
          setSlug(`/schedules/${scheduleForSearch}/stats`)
        }
        return
      }

      // Switch to stats tab
      setActiveTab('stats')
      // Set the sort_by URL param
      setParam('sn_sort_by', statId)
      // Update the sortBy state
      setSortBy(statId)
      // Set the direction based on the stat's is_sort_desc
      setDirection(expectedDirection)

      // Ensure division filter is reflected in Stats Filter
      if (scheduleForSearch) {
        setIsGlobalFilterEnabled(false)
        setSlug(`/schedules/${scheduleForSearch}/stats`)
      }

      // Clear items and trigger loading
      setItems([])
      setLoading(true)
    },
    [
      setParam,
      statTypesById,
      statIdByAbbr,
      sortBy,
      direction,
      scheduleForSearch,
    ]
  )

  useEffect(() => {
    if (currentParams.sn_team) {
      setSlug(`/schedules/${entityId}/stats/team/${currentParams.sn_team}`)
    }
  }, [currentParams.sn_team, entityId])

  // Update sortBy when settings become available (only once per schedule load)
  useEffect(() => {
    // Check if settings are loaded (playerSettings exists and has sort_order)
    const hasPlayerSettings = playerSettings?.sort_order?.stat_id
    const hasGoalieSettings = goalieSettings?.sort_order?.stat_id

    if (
      !settingsAppliedRef.current &&
      (hasPlayerSettings || hasGoalieSettings)
    ) {
      if (!isGoalieStats && hasPlayerSettings) {
        setSortBy(playerDefaultSort)
        setParam('sn_sort_by', playerDefaultSort)
      } else if (isGoalieStats && hasGoalieSettings) {
        setSortBy(goalieDefaultSort)
        setParam('sn_sort_by', goalieDefaultSort)
      }

      // This is a bit hacky, but its the only way I've found to make the default sort work and the component
      // re-render with the correct sort order
      setIsGoalieStats(false)
      setParam('sn_sort_by', playerDefaultSort)
      setSortBy(playerDefaultSort)

      settingsAppliedRef.current = true
    }
  }, [
    playerSettings,
    goalieSettings,
    playerDefaultSort,
    goalieDefaultSort,
    isGoalieStats,
    setParam,
  ])

  // Reset settings applied flag when entityId changes (new schedule)
  useEffect(() => {
    settingsAppliedRef.current = false
    setItems([])
    setLoading(true)
  }, [entityId, entityType])

  const getItems = useCallback(
    async (page, overrideSearchTerm, mode = 'reset') => {
      const isAppend = mode === 'append'

      if (!settingsAppliedRef.current) {
        return
      }

      if (!sortBy) {
        return
      }

      if (isAppend) {
        setIsAppending(true)
      } else {
        setLoading(true)
      }

      setError(null)

      try {
        let searchSortBy = sortBy
        if (isGoalieStats && sortBy === playerDefaultSort) {
          searchSortBy = goalieDefaultSort
        }
        const searchTerm =
          overrideSearchTerm !== undefined
            ? overrideSearchTerm
            : debouncedSearchTerm
        const response = await req(slug, {
          query: {
            page,
            sortBy: searchSortBy,
            sort: direction,
            goalie: isGoalieStats ? 1 : 0,
            ...(scheduleForSearch && { division_id: scheduleForSearch }),
            ...(isGlobalFilterEnabled && { global: 1 }),
            ...(searchTerm && { player_name: searchTerm }),
          },
        })

        const nextData = response?.data ?? []
        setItems((prev) => (isAppend ? [...prev, ...nextData] : nextData))

        const pg = response?.meta?.pagination

        const cp = pg?.current_page ?? page ?? 1
        setCurrentPage(Number(cp))

        const per = pg?.per_page ?? nextData.length
        const total = pg?.count ?? 0
        setHasMore(total ? cp * per < total : nextData.length > 0)
      } catch (e) {
        setError(e.message)
      } finally {
        if (isAppend) {
          setIsAppending(false)
        } else {
          setLoading(false)
        }
      }
    },
    [
      sortBy,
      isGoalieStats,
      playerDefaultSort,
      slug,
      direction,
      scheduleForSearch,
      isGlobalFilterEnabled,
      debouncedSearchTerm,
      goalieDefaultSort,
      isAggregateEnabled,
    ]
  )

  useEffect(() => {
    if (!loaded) {
      return
    }
    // Skip fetching if not on stats tab to avoid unnecessary API calls
    if (activeTab !== 'stats') {
      setLoading(false)
      return
    }
    if (manualSearchRef.current) {
      manualSearchRef.current = false
      return
    }
    setLoading(true)
    setCurrentPage(1)
    setHasMore(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
    getItems(1, undefined, 'reset')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loaded,
    direction,
    slug,
    entityId,
    sortBy,
    isGoalieStats,
    refreshKey,
    scheduleForSearch,
    isGlobalFilterEnabled,
    debouncedSearchTerm,
    isAggregateEnabled,
    activeTab,
  ])

  const handleScroll = useCallback(() => {
    // Only trigger infinite scroll on stats tab
    if (activeTab !== 'stats') {
      return
    }
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && hasMore && !loading && !isAppending) {
      getItems(currentPage + 1, undefined, 'append')
    }
  }, [activeTab, hasMore, loading, isAppending, currentPage, getItems])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  const commonColumns = useMemo(
    () => [
      {
        title: (
          <Tooltip title='Rank' placement='top' mouseEnterDelay={0.1}>
            <span
              css={css`
                display: block;
                width: 100%;
              `}
            >
              #
            </span>
          </Tooltip>
        ),
        key: 'index',
        width: 50,
        align: 'center',
        fixed: 'left',
        render: (_, __, index) => {
          return <Typography.Text>{index + 1}</Typography.Text>
        },
      },
      {
        title: 'Player',
        key: 'player',
        dataIndex: 'player',
        sorter: (a, b, sortOrder) => {},
        showSorterTooltip: false,
        align: 'left',
        sortOrder:
          sortBy === 'player.full_name'
            ? direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (item) => {
          return (
            <Link
              to={`${ROUTES.PLAYER_ROOT}/${item?.player?.id}`}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                max-width: 270px;
              `}
            >
              <Avatar
                src={
                  getImageThumbnailId(item?.player) ||
                  '/images/placeholders/hockey/player.png'
                }
                shape='square'
                icon={<Icon name='user' fontSize={14} />}
                css={css`
                  border: 1px solid #dbdbdb;
                  background-color: white;
                  margin: 0;
                `}
              />
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  min-width: 0;
                `}
              >
                <Typography.Text
                  strong
                  ellipsis
                  css={css`
                    color: ${colors.NEUTRAL_0};
                  `}
                >
                  {item?.player?.name_first} {item?.player?.name_last}
                </Typography.Text>
                {item?.player?.player_number ? (
                  <Typography.Text
                    css={css`
                      font-size: 12px;
                      color: ${colors.NEUTRAL_50} !important;
                    `}
                  >
                    #{item?.player?.player_number}
                  </Typography.Text>
                ) : (
                  <div
                    css={css`
                      height: 12px;
                    `}
                  />
                )}
              </div>
            </Link>
          )
        },
        fixed: isMobile ? 'sticky' : 'sticky',
        width: isMobile ? 200 : 270,
        sortDirections: ['ascend', 'descend', 'ascend'],
      },
      {
        title: 'Team',
        dataIndex: 'team',
        sorter: (a, b, sortOrder) => {},
        showSorterTooltip: false,
        sortOrder:
          sortBy === 'team.name'
            ? direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (item) => (
          <Link
            // to={`/team/${item?.team?.id}?schedule_id=${item?.schedule?.id}`}
            to={`/team/${item?.team?.id}?sn_schedule=${entityId}`}
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              max-width: 270px;
            `}
          >
            <Avatar
              src={
                getImageThumbnailId(item?.team) ||
                '/images/placeholders/hockey/team.png'
              }
              icon={<Icon name='user-friends' fontSize={14} />}
              shape='square'
              css={css`
                border: 1px solid #dbdbdb;
                background-color: white;
                padding: 2px;
              `}
            />
            <Typography.Text strong ellipsis>
              {item?.team?.name_full}
            </Typography.Text>
          </Link>
        ),
        key: 'team',
        width: 270,
        sortDirections: ['ascend', 'descend', 'ascend'],
      },
    ],
    [isMobile, entityId, sortBy, direction]
  )

  // Dynamic skater columns - use settings when available, fallback to static
  const skaterColumns = useMemo(() => {
    if (playerDisplayedIds.length > 0) {
      return generateColumnsFromIds(playerDisplayedIds, sortBy, direction)
    }
    return []
  }, [playerDisplayedIds, sortBy, direction, generateColumnsFromIds])

  // Dynamic goalie columns - use settings when available, fallback to static
  const goalieColumns = useMemo(() => {
    if (goalieDisplayedIds.length > 0) {
      return generateColumnsFromIds(goalieDisplayedIds, sortBy, direction)
    }
    return []
  }, [goalieDisplayedIds, sortBy, direction, generateColumnsFromIds])

  // Dynamic player columns (soccer) - use settings when available, fallback to static
  const playerColumns = useMemo(() => {
    if (playerDisplayedIds.length > 0) {
      return generateColumnsFromIds(playerDisplayedIds, sortBy, direction)
    }
    return []
  }, [playerDisplayedIds, sortBy, direction, generateColumnsFromIds])

  // Dynamic keeper columns (soccer goalie) - use settings when available, fallback to static
  const keeperColumns = useMemo(() => {
    if (goalieDisplayedIds.length > 0) {
      return generateColumnsFromIds(goalieDisplayedIds, sortBy, direction)
    }
    return []
  }, [goalieDisplayedIds, sortBy, direction, generateColumnsFromIds])

  // Create union of all displayed abbreviations for dynamic data mapping
  const allDisplayedAbbrs = useMemo(() => {
    const allIds = [
      ...(playerDisplayedIds ?? []),
      ...(goalieDisplayedIds ?? []),
    ]
    const uniqueIds = [...new Set(allIds)]
    return uniqueIds
      .map((id) => statTypesById[id]?.abbreviation)
      .filter(Boolean)
  }, [playerDisplayedIds, goalieDisplayedIds, statTypesById])

  const data = useMemo(
    () => [
      ...items.map((item, index) => {
        const itemStats = item?.stats
        const row = {
          key: index,
          player: item,
          team: item,
          PN: item?.player?.player_number,
          // SKATER DATA (kept for backward compatibility)
          GP: getStatValue(itemStats, 'GP'),
          G: getStatValue(itemStats, 'G'),
          A: getStatValue(itemStats, 'A'),
          P: getStatValue(itemStats, 'P'),
          PiM: getStatValue(itemStats, 'PiM'),
          PPG: getStatValue(itemStats, 'PPG'),
          'PTS/G': getStatValue(itemStats, 'PTS/G'),
          // GOALIE DATA (kept for backward compatibility)
          W: getStatValue(itemStats, 'W'),
          L: getStatValue(itemStats, 'L'),
          T: getStatValue(itemStats, 'T'),
          OTL: getStatValue(itemStats, 'OTL'),
          GAA: getStatValue(itemStats, 'GAA'),
          'SV%': getStatValue(itemStats, 'SV%'),
          SO: getStatValue(itemStats, 'SO'),
          MP: getStatValue(itemStats, 'MP'),
          SA: getStatValue(itemStats, 'SA'),
          SV: getStatValue(itemStats, 'SV'),
          GA: getStatValue(itemStats, 'GA'),
          // SPECIFIC SOCCER DATA (kept for backward compatibility)
          'G/M': getStatValue(itemStats, 'G/M'),
          GCA: getStatValue(itemStats, 'GCA'),
          YC: getStatValue(itemStats, 'YC'),
          FC: getStatValue(itemStats, 'FC'),
          GC: getStatValue(itemStats, 'GC'),
          FG: getStatValue(itemStats, 'FG'),
          RC: getStatValue(itemStats, 'RC'),
          BC: getStatValue(itemStats, 'BC'),
          TP: getStatValue(itemStats, 'TP'),
          CS: getStatValue(itemStats, 'CS'),
          D: getStatValue(itemStats, 'D'),
          Mins: getStatValue(itemStats, 'Mins'),
          GF: getStatValue(itemStats, 'GF'),
          GFA: getStatValue(itemStats, 'GFA'),
          OTW: getStatValue(itemStats, 'OTW'),
          SOL: getStatValue(itemStats, 'SOL'),
        }

        // Dynamically populate all displayed abbreviations
        allDisplayedAbbrs.forEach((abbr) => {
          if (!(abbr in row)) {
            row[abbr] = getStatValue(itemStats, abbr)
          }
        })

        return row
      }),
    ],
    [items, getStatValue, allDisplayedAbbrs]
  )

  const columns = useMemo(() => {
    if (isGoalieStats) {
      return commonColumns.concat(isSoccer ? keeperColumns : goalieColumns)
    }
    return commonColumns.concat(isSoccer ? playerColumns : skaterColumns)
  }, [
    isGoalieStats,
    isSoccer,
    commonColumns,
    goalieColumns,
    keeperColumns,
    playerColumns,
    skaterColumns,
  ])

  const renderTable = useCallback(() => {
    return (
      <Table
        columns={columns}
        bordered
        size='small'
        scroll={{ x: 1300 }}
        pagination={false}
        dataSource={data}
        onChange={(paginationInfo, tableFilters, sorter) => {
          const columnKeyMap = {
            player: 'player.full_name',
            team: 'team.name',
          }
          const key = sorter?.columnKey
          const mappedKey = columnKeyMap[key] || statIdByAbbr[key] || key
          setSortBy(mappedKey)
          setDirection(sorter?.order === 'descend' ? 'desc' : 'asc')
        }}
        css={css`
          .ant-table-cell {
            color: ${colors.NEUTRAL_0} !important;
          }
          .ant-table-measure-row {
            display: none !important;
          }
          .ant-table-thead > tr > th {
            padding: 8px 8px !important;
            background-color: ${colors.SECONDARY_300} !important;
          }
          .ant-table-thead > tr > th.ant-table-column-sort {
            isolation: isolate;
            background-color: transparent !important;
            position: relative;
          }
          .ant-table-thead > tr > th.ant-table-column-sort::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: ${colors.SECONDARY_300};
            background-image: linear-gradient(
              ${colors.INFO_500},
              ${colors.INFO_500}
            );
            background-blend-mode: color-dodge;
            z-index: -1;
            pointer-events: none;
          }
          .ant-table-tbody > tr:nth-child(even) {
            background-color: ${colors.SECONDARY_400};
          }
          .ant-table-tbody > tr:nth-child(odd) {
            background-color: ${colors.SECONDARY_300};
          }
          .ant-table-tbody > tr > td {
            padding: 8px 8px !important;
            vertical-align: middle;
          }
          .ant-table-tbody > tr > td.ant-table-column-sort {
            isolation: isolate;
            background-color: transparent !important;
          }
          .ant-table-tbody
            > tr
            > td.ant-table-column-sort:not(.ant-table-cell-fix-left):not(
              .ant-table-cell-fix-right
            ) {
            position: relative;
          }
          .ant-table-tbody > tr > td.ant-table-column-sort::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-image: linear-gradient(
              ${colors.INFO_500},
              ${colors.INFO_500}
            );
            background-blend-mode: color-dodge;
            z-index: -1;
            pointer-events: none;
          }
          .ant-table-tbody
            > tr:nth-child(even)
            > td.ant-table-column-sort::before {
            background-color: ${colors.SECONDARY_400};
          }
          .ant-table-tbody
            > tr:nth-child(odd)
            > td.ant-table-column-sort::before {
            background-color: ${colors.SECONDARY_300};
          }
          /* Override sticky column backgrounds when sorted */
          .ant-table-tbody
            > tr
            > td.ant-table-column-sort.ant-table-cell-fix-left {
            background-color: transparent !important;
          }
          @media (max-width: 768px) {
            padding: 0 16px !important;
          }
          .ant-table-tbody
            > tr:nth-child(even)
            > td.ant-table-cell.ant-table-cell-fix-left.ant-table-cell-fix-left-last {
            background-color: ${colors.SECONDARY_400} !important;
          }
          .ant-table-tbody
            > tr:nth-child(odd)
            > td.ant-table-cell.ant-table-cell-fix-left.ant-table-cell-fix-left-last {
            background-color: ${colors.SECONDARY_300} !important;
          }
          .ant-table-tbody > tr:nth-child(even) > td:nth-child(1) {
            background-color: ${colors.SECONDARY_400} !important;
          }
          .ant-table-tbody > tr:nth-child(odd) > td:nth-child(1) {
            background-color: ${colors.SECONDARY_300} !important;
          }
        `}
      />
    )
  }, [data, columns, statIdByAbbr])

  const renderGoalieTable = useCallback(() => {
    return (
      <Table
        columns={columns}
        bordered
        size='small'
        scroll={{ x: 1300 }}
        pagination={false}
        dataSource={data}
        onChange={(paginationInfo, tableFilters, sorter) => {
          const columnKeyMap = {
            player: 'player.full_name',
            team: 'team.name',
          }
          const key = sorter?.columnKey
          const mappedKey = columnKeyMap[key] || statIdByAbbr[key] || key
          setSortBy(mappedKey)
          setDirection(sorter?.order === 'descend' ? 'desc' : 'asc')
        }}
        css={css`
          .ant-table-cell {
            color: ${colors.NEUTRAL_0} !important;
          }
          .ant-table-measure-row {
            display: none !important;
          }
          .ant-table-thead > tr > th {
            padding: 8px 8px !important;
            background-color: ${colors.SECONDARY_300} !important;
          }
          .ant-table-thead > tr > th.ant-table-column-sort {
            isolation: isolate;
            background-color: transparent !important;
            position: relative;
          }
          .ant-table-thead > tr > th.ant-table-column-sort::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: ${colors.SECONDARY_300};
            background-image: linear-gradient(
              var(--Semantics-Info-info-500, #225a9c),
              var(--Semantics-Info-info-500, #225a9c)
            );
            background-blend-mode: color-dodge;
            z-index: -1;
            pointer-events: none;
          }
          .ant-table-tbody > tr:nth-child(even) {
            background-color: ${colors.SECONDARY_400};
          }
          .ant-table-tbody > tr:nth-child(odd) {
            background-color: ${colors.SECONDARY_300};
          }
          .ant-table-tbody > tr > td {
            padding: 8px 8px !important;
            vertical-align: middle;
          }
          .ant-table-tbody > tr > td.ant-table-column-sort {
            isolation: isolate;
            background-color: transparent !important;
          }
          .ant-table-tbody
            > tr
            > td.ant-table-column-sort:not(.ant-table-cell-fix-left):not(
              .ant-table-cell-fix-right
            ) {
            position: relative;
          }
          .ant-table-tbody > tr > td.ant-table-column-sort::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-image: linear-gradient(
              var(--Semantics-Info-info-500, #225a9c),
              var(--Semantics-Info-info-500, #225a9c)
            );
            background-blend-mode: color-dodge;
            z-index: -1;
            pointer-events: none;
          }
          .ant-table-tbody
            > tr:nth-child(even)
            > td.ant-table-column-sort::before {
            background-color: ${colors.SECONDARY_400};
          }
          .ant-table-tbody
            > tr:nth-child(odd)
            > td.ant-table-column-sort::before {
            background-color: ${colors.SECONDARY_300};
          }
          /* Override sticky column backgrounds when sorted */
          .ant-table-tbody
            > tr
            > td.ant-table-column-sort.ant-table-cell-fix-left {
            background-color: transparent !important;
          }
          @media (max-width: 768px) {
            padding: 0 16px !important;
          }
          .ant-table-tbody
            > tr:nth-child(even)
            > td.ant-table-cell.ant-table-cell-fix-left.ant-table-cell-fix-left-last {
            background-color: ${colors.SECONDARY_400} !important;
          }
          .ant-table-tbody
            > tr:nth-child(odd)
            > td.ant-table-cell.ant-table-cell-fix-left.ant-table-cell-fix-left-last {
            background-color: ${colors.SECONDARY_300} !important;
          }
          .ant-table-tbody > tr:nth-child(even) > td:nth-child(1) {
            background-color: ${colors.SECONDARY_400} !important;
          }
          .ant-table-tbody > tr:nth-child(odd) > td:nth-child(1) {
            background-color: ${colors.SECONDARY_300} !important;
          }
        `}
      />
    )
  }, [data, columns, statIdByAbbr])

  return (
    <>
      {/* <ReprocessingOverlay
        scheduleReprocessingListener={scheduleReprocessingListener}
      /> */}
      <ReprocessingSettingsOverlay
        scheduleReprocessingListener={scheduleReprocessingListener}
        setOutsideDismissed={setOutsideDismissed}
      />
      <div
        css={css`
          margin-top: 24px;

          ${scheduleReprocessingListener &&
          !dismissed &&
          css`
            opacity: 0.7;
            filter: blur(2px);
            pointer-events: none;
          `}
        `}
      >
        {/* Game filters - shown for both tabs */}
        <GameListFilters
          filters={filters}
          team_id={entityId}
          setSlug={setSlug}
          state={state}
          setState={setState}
          loading={loading}
          teams={activeTab === 'leaders' ? [] : teams}
          team={team}
          orgId={orgId}
          scheduleId={entityId}
          onTeamChange={onTeamChange}
          onScheduleChange={onScheduleChange}
          isHidden={areFiltersHidden}
          displayLeaderboards={displayLeaderboards}
          selected={activeTab}
          onSelect={handleTabSelect}
          disabled={loading}
          displayLabels={false}
          compactFilters
          childrenInFirstRow
        >
          <StatisticsFilters
            loading={loading}
            isGenericSport={isGenericSport}
            isSoccer={isSoccer}
            isHockey={isHockey}
            isGoalieStats={isGoalieStats}
            onPlayerTypeChange={(isGoalie) => {
              if (isGoalie) {
                setParam('sn_sort_by', goalieDefaultSort)
                onIsGoalie(true)
                setDirection(
                  statTypesById[goalieDefaultSort]?.is_sort_desc
                    ? 'desc'
                    : 'asc'
                )
                setSortBy(goalieDefaultSort)
              } else {
                setParam('sn_sort_by', playerDefaultSort)
                onIsGoalie(false)
                setDirection(
                  statTypesById[playerDefaultSort]?.is_sort_desc
                    ? 'desc'
                    : 'asc'
                )
                setSortBy(playerDefaultSort)
              }
            }}
            showStatsFilter={
              activeTab !== 'leaders' && isSchedule && !!scheduleForSearch
            }
            isGlobalFilterEnabled={isGlobalFilterEnabled}
            onGlobalFilterChange={(enabled) => {
              setIsGlobalFilterEnabled(enabled)
              if (enabled) {
                setSlug(BASE_SLUG)
              } else {
                if (scheduleForSearch && isSchedule) {
                  setSlug(`/schedules/${scheduleForSearch}/stats`)
                }
              }
            }}
            displayLabels={false}
          />
        </GameListFilters>
        {/* Tab content */}
        {displayLeaderboards && activeTab === 'leaders' ? (
          <LeadersView
            scheduleId={entityId}
            isGoalieStats={isGoalieStats}
            scheduleForSearch={scheduleForSearch}
            isGlobalFilterEnabled={isGlobalFilterEnabled}
            onViewMore={handleLeaderViewMore}
          />
        ) : (
          <>
            {error ? (
              <div
                css={css`
                  margin-top: 100px;
                  text-align: center;
                  display: flex;
                  flex-direction: column;
                  gap: 12px;
                  align-items: center;
                `}
              >
                <Typography.Text type='danger'>{String(error)}</Typography.Text>
                <Button
                  text='Refresh'
                  variant='primary'
                  onClick={() => getItems(1, undefined, 'reset')}
                  isLoading={loading}
                  disabled={loading}
                />
              </div>
            ) : (
              <div>
                <div
                  css={css`
                    margin-bottom: 16px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex-wrap: wrap;
                  `}
                >
                  <div>
                    <Input
                      prefix={
                        <SearchOutlined
                          css={css`
                            font-size: 16px;
                            color: ${colors.NEUTRAL_50};
                          `}
                        />
                      }
                      allowClear={true}
                      placeholder='Search Player'
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      css={css`
                        width: 300px;
                        background-color: ${colors.SECONDARY_500};
                        border: 1px solid ${colors.SECONDARY_200};
                        border-radius: 8px;
                        color: ${colors.WHITE};
                        font-size: 14px;
                        font-weight: 400;
                        line-height: 20.3px;
                        @media (max-width: 768px) {
                          width: 100%;
                        }
                        .ant-input {
                          background-color: ${colors.SECONDARY_500};
                          color: ${colors.WHITE};
                        }
                        .ant-input-prefix {
                          color: ${colors.NEUTRAL_100};
                        }
                        &::placeholder {
                          color: ${colors.NEUTRAL_100};
                        }
                      `}
                    />
                  </div>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      // https://sportninja.atlassian.net/browse/SN-6937
                      display: none;
                    `}
                  >
                    <Text
                      variant='paragraphSmall'
                      weight='regular'
                      color={colors.WHITE}
                    >
                      Combine Player Stats
                    </Text>
                    <Switch
                      size='small'
                      checked={isAggregateEnabled}
                      onChange={(checked) => {
                        setIsAggregateEnabled(checked)
                        setParam('sn_aggregate', checked ? '1' : '0')
                      }}
                      css={css`
                        &.ant-switch-checked {
                          background-color: ${colors.CAUTION_MEDIUM};
                        }
                      `}
                    />
                  </div>
                </div>
                {/* This is done like this because of: https://github.com/ant-design/ant-design/issues/35408 */}
                {loading ? (
                  <div
                    css={css`
                      display: flex;
                      justify-content: center;
                      align-items: center;
                      padding: 100px 0;
                    `}
                  >
                    <Spin size='small' />
                  </div>
                ) : (
                  <>
                    {!isGoalieStats ? renderTable() : null}
                    {isGoalieStats ? renderGoalieTable() : null}
                  </>
                )}

                {/* Infinite scroll footer */}
                <div
                  css={css`
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 24px 0;
                    min-height: 60px;
                  `}
                >
                  {isAppending && (
                    <Flex
                      css={css`
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                      `}
                    >
                      <Spin size='small' />
                      <Typography.Text
                        css={css`
                          color: ${colors.NEUTRAL_50};
                          font-size: 14px;
                          line-height: 1;
                        `}
                      >
                        Loading More...
                      </Typography.Text>
                    </Flex>
                  )}
                  {!hasMore && !loading && items.length > 0 && (
                    <Typography.Text
                      css={css`
                        color: ${colors.NEUTRAL_50};
                        font-size: 14px;
                      `}
                    >
                      No More Results
                    </Typography.Text>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <FloatButton.BackTop
        icon={<ArrowUpOutlined />}
        css={css`
          .ant-float-btn-body {
            background-color: ${colors.PRIMARY} !important;
          }
          .ant-float-btn-icon {
            color: ${PRIMARY_BUTTON_CONTENT.icon} !important;
          }
        `}
      />
    </>
  )
}

const mapStateToProps = (state, { sportId }) => {
  return {
    statTypes: getStatTypesBySportId(state, sportId),
  }
}

const mapDispatchToProps = (_) => {
  return {}
}

export default connect(mapStateToProps, mapDispatchToProps)(Statistics)
